import { createContext, useContext, useMemo, useState } from "react";

// Shared date-filter for the Admin Dashboard. Introduced so the Profit
// Dashboard, Expense Tracker, Other Income Tracker, and Orders tab all read
// from ONE window — merchant picks "This Month" once and every panel updates
// consistently, so the top summary cards and per-tab lists can never drift.

const AdminFilterContext = createContext(null);

// Same date maths used to live inside AdminDashboard.jsx. Kept here so any
// consumer can compute the params it should send to /admin/orders-report or
// use for its own client-side filtering.
export function computeReportParams(dateFilter, customFrom, customTo) {
  const today = new Date();
  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const y = today.getFullYear();
  const m = today.getMonth();
  if (dateFilter === "this-month") return { date_from: iso(new Date(y, m, 1)), date_to: iso(new Date(y, m + 1, 0)) };
  if (dateFilter === "last-month") return { date_from: iso(new Date(y, m - 1, 1)), date_to: iso(new Date(y, m, 0)) };
  if (dateFilter === "last-3-months") return { date_from: iso(new Date(y, m - 2, 1)), date_to: iso(new Date(y, m + 1, 0)) };
  if (dateFilter === "this-year") return { date_from: `${y}-01-01`, date_to: `${y}-12-31` };
  if (dateFilter === "last-year") return { date_from: `${y - 1}-01-01`, date_to: `${y - 1}-12-31` };
  if (dateFilter === "custom") return { date_from: customFrom || "", date_to: customTo || "" };
  return { date_from: "", date_to: "" };
}

export const PROFIT_DATE_FILTERS = [
  { key: "this-month",    label: "This Month" },
  { key: "last-month",    label: "Last Month" },
  { key: "last-3-months", label: "Last 3 Months" },
  { key: "this-year",     label: "This Year" },
  { key: "last-year",     label: "Last Year" },
  { key: "lifetime",      label: "Lifetime" },
  { key: "custom",        label: "Custom" },
];

export function AdminFilterProvider({ children }) {
  const [dateFilter, setDateFilter] = useState("this-month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const params = useMemo(
    () => computeReportParams(dateFilter, customFrom, customTo),
    [dateFilter, customFrom, customTo],
  );
  const filterLabel = PROFIT_DATE_FILTERS.find((f) => f.key === dateFilter)?.label || "";
  return (
    <AdminFilterContext.Provider
      value={{ dateFilter, setDateFilter, customFrom, setCustomFrom, customTo, setCustomTo, params, filterLabel }}
    >
      {children}
    </AdminFilterContext.Provider>
  );
}

export function useAdminFilter() {
  const ctx = useContext(AdminFilterContext);
  if (!ctx) throw new Error("useAdminFilter must be used inside <AdminFilterProvider>");
  return ctx;
}
